import React from 'react';
import Select from 'react-select';
import CreatableSelect from 'react-select/creatable';
import { baseURL } from '../../api/api';
import styles from './Form.module.css';

/**
 * Поля форм админки.
 *
 * Валидации в прежних формах не было вовсе: атрибут required="required" стоял
 * на инпутах вне тега <form> и ни на что не влиял, поэтому пустую статью можно
 * было отправить. Здесь ошибка поля приходит с сервера (422) и показывается
 * рядом с самим полем.
 */

export const Field = ({ label, required, hint, error, htmlFor, children }) => (
    <div className="adm-field">
        {label && (
            <label className="adm-label" htmlFor={htmlFor}>
                {label}
                {required && <span className="adm-required">*</span>}
            </label>
        )}
        {children}
        {hint && !error && <div className="adm-hint">{hint}</div>}
        {error && <div className="adm-error">{error}</div>}
    </div>
);

export const TextInput = React.forwardRef(({ label, required, hint, error, ...props }, ref) => (
    <Field label={label} required={required} hint={hint} error={error} htmlFor={props.id}>
        <input
            ref={ref}
            type="text"
            className={`adm-input ${error ? 'adm-input-invalid' : ''}`}
            {...props}
        />
    </Field>
));

export const TextArea = ({ label, required, hint, error, rows = 4, ...props }) => (
    <Field label={label} required={required} hint={hint} error={error} htmlFor={props.id}>
        <textarea
            rows={rows}
            className={`adm-textarea ${error ? 'adm-textarea-invalid' : ''}`}
            {...props}
        />
    </Field>
);

export const SelectInput = ({ label, required, hint, error, options, placeholder = '— не выбрано —', ...props }) => (
    <Field label={label} required={required} hint={hint} error={error} htmlFor={props.id}>
        <select className={`adm-select ${error ? 'adm-select-invalid' : ''}`} {...props}>
            <option value="">{placeholder}</option>
            {options.map((o) => (
                <option key={o.value} value={o.value}>
                    {o.label}
                </option>
            ))}
        </select>
    </Field>
);

export const Checkbox = ({ label, hint, checked, onChange, id }) => (
    <div className="adm-field">
        <label className="adm-checkbox" htmlFor={id}>
            <input id={id} type="checkbox" checked={checked} onChange={(e) => onChange(e.target.checked)} />
            <span>{label}</span>
        </label>
        {hint && <div className="adm-hint">{hint}</div>}
    </div>
);

/**
 * Нормализация для поиска в справочниках: регистр, «ё», пробелы и знаки препинания
 * не важны. «А. В. Акаев» находит «А.В. Акаев», «банки,» — «банки».
 */
export const normalizeName = (s) =>
    String(s || '')
        .toLowerCase()
        .replace(/ё/g, 'е')
        .replace(/[\s.,;:'"«»()\-–—]+/g, '');

const filterOption = (option, input) => {
    if (!input) return true;
    if (option.data && option.data.__isNew__) return true;
    return normalizeName(option.label).includes(normalizeName(input));
};

/**
 * Множественный выбор связей (авторы, ключевые слова, кафедры).
 * Значение — массив id, чтобы страницы не работали с внутренним форматом react-select.
 *
 * С onCreate в списке появляется «+ Добавить «…»»: новую запись справочника можно
 * завести, не уходя из формы. Пункт не предлагается, если такая запись уже есть
 * (с точностью до пробелов и знаков препинания), — так не плодятся дубли.
 */
export const MultiSelect = ({ label, required, hint, error, options, value, onChange, placeholder, isLoading, onCreate }) => {
    const selected = value.map((id) => options.find((o) => o.value === id)).filter(Boolean);
    const [creating, setCreating] = React.useState(false);

    const common = {
        isMulti: true,
        isLoading: isLoading || creating,
        isDisabled: creating,
        options,
        value: selected,
        onChange: (picked) => onChange((picked || []).map((p) => p.value)),
        placeholder: placeholder || (onCreate ? 'Начните вводить: найдём или добавим' : 'Начните вводить для поиска'),
        noOptionsMessage: () => 'Ничего не найдено',
        loadingMessage: () => 'Загрузка…',
        filterOption,
        classNamePrefix: 'select',
        styles: selectStyles(Boolean(error)),
    };

    if (!onCreate) {
        return (
            <Field label={label} required={required} hint={hint} error={error}>
                <Select {...common} />
            </Field>
        );
    }

    const handleCreate = async (input) => {
        const name = input.trim();
        if (!name) return;
        setCreating(true);
        try {
            const created = await onCreate(name);
            if (created) onChange([...value, created.value]);
        } finally {
            setCreating(false);
        }
    };

    return (
        <Field label={label} required={required} hint={hint} error={error}>
            <CreatableSelect
                {...common}
                onCreateOption={handleCreate}
                formatCreateLabel={(input) => `+ Добавить «${input.trim()}»`}
                isValidNewOption={(input) => {
                    const n = normalizeName(input);
                    return n.length > 1 && !options.some((o) => normalizeName(o.label) === n);
                }}
                createOptionPosition="first"
            />
        </Field>
    );
};

export const SingleSelect = ({ label, required, hint, error, options, value, onChange, placeholder, isLoading, isClearable = true }) => {
    const selected = options.find((o) => String(o.value) === String(value)) || null;

    return (
        <Field label={label} required={required} hint={hint} error={error}>
            <Select
                isLoading={isLoading}
                isClearable={isClearable}
                options={options}
                value={selected}
                onChange={(picked) => onChange(picked ? picked.value : '')}
                placeholder={placeholder || '— не выбрано —'}
                noOptionsMessage={() => 'Ничего не найдено'}
                loadingMessage={() => 'Загрузка…'}
                classNamePrefix="select"
                styles={selectStyles(Boolean(error))}
            />
        </Field>
    );
};

const selectStyles = (invalid) => ({
    control: (base, state) => ({
        ...base,
        minHeight: 40,
        borderColor: invalid ? '#c0392b' : state.isFocused ? '#b02a2a' : '#c4cad1',
        boxShadow: state.isFocused ? '0 0 0 3px #fbecec' : 'none',
        '&:hover': { borderColor: invalid ? '#c0392b' : '#b02a2a' },
    }),
    menu: (base) => ({ ...base, zIndex: 30 }),
});

/**
 * Файловое поле: показывает уже загруженный файл, выбранный новый и прогресс отправки.
 * Раньше при загрузке PDF на сотни мегабайт был только крутящийся индикатор
 * без единого признака того, что что-то происходит.
 */
export const FileField = ({
    label,
    hint,
    error,
    accept,
    currentPath,
    currentLabel = 'Текущий файл',
    file,
    onChange,
    progress,
    id,
}) => (
    <Field label={label} hint={hint} error={error} htmlFor={id}>
        {currentPath && (
            <div className={styles.currentFile}>
                <span className="adm-muted">{currentLabel}:</span>{' '}
                <a href={baseURL + currentPath} target="_blank" rel="noopener noreferrer">
                    {currentPath.split('/').pop()}
                </a>
            </div>
        )}

        <label className={styles.fileDrop} htmlFor={id}>
            <input
                id={id}
                type="file"
                accept={accept}
                className={styles.fileInput}
                onChange={(e) => onChange(e.target.files[0] || null)}
            />
            <span className={styles.fileButton}>Выбрать файл</span>
            <span className={styles.fileName}>
                {file ? `${file.name} · ${formatSize(file.size)}` : 'Файл не выбран'}
            </span>
            {file && (
                <button
                    type="button"
                    className={styles.fileClear}
                    onClick={(e) => {
                        e.preventDefault();
                        onChange(null);
                    }}
                >
                    Убрать
                </button>
            )}
        </label>

        {typeof progress === 'number' && progress > 0 && progress < 100 && (
            <div className="adm-progress" title={`${progress}%`}>
                <div className="adm-progress-bar" style={{ width: `${progress}%` }} />
            </div>
        )}
    </Field>
);

const formatSize = (bytes) => {
    if (bytes < 1024) return `${bytes} Б`;
    if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} КБ`;
    return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
};

export const FormActions = ({ children }) => <div className={styles.actions}>{children}</div>;

export const FormSection = ({ title, description, children }) => (
    <section className={styles.section}>
        {title && <h2 className={styles.sectionTitle}>{title}</h2>}
        {description && <p className={styles.sectionDescription}>{description}</p>}
        {children}
    </section>
);
